import {
  AccountStateConflictError,
  AccountStateConflictErrorType,
  BaseError,
  InvalidAddressError,
  InvalidAddressErrorType,
  NumberToHexErrorType,
  RpcAccountStateOverride,
  RpcStateMapping,
  RpcStateOverride,
  StateAssignmentConflictError,
  StateAssignmentConflictErrorType,
  StateMapping,
  StateOverride,
  getAddress,
  isAddress,
  numberToHex,
} from 'viem'

export type SerializeStateMappingParameters = StateMapping | undefined

export type SerializeStateMappingErrorType = InvalidBytesLengthErrorType
export type InvalidBytesLengthErrorType = InvalidBytesLengthError & {
  name: 'InvalidBytesLengthError'
}
export class InvalidBytesLengthError extends BaseError {
  override name = 'InvalidBytesLengthError'
  constructor({
    size,
    targetSize,
    type,
  }: {
    size: number
    targetSize: number
    type: 'hex' | 'bytes'
  }) {
    super(
      `${type.charAt(0).toUpperCase()}${type
        .slice(1)
        .toLowerCase()} is expected to be ${targetSize} ${type} long, but is ${size} ${type} long.`,
    )
  }
}

export function serializeStateMapping(stateMapping: SerializeStateMappingParameters): RpcStateMapping | undefined {
  if (!stateMapping || stateMapping.length === 0) return undefined
  return stateMapping.reduce((acc, { slot, value }) => {
    if (slot.length !== 66)
      throw new InvalidBytesLengthError({
        size: slot.length,
        targetSize: 66,
        type: 'hex',
      })
    if (value.length !== 66)
      throw new InvalidBytesLengthError({
        size: value.length,
        targetSize: 66,
        type: 'hex',
      })
    acc[slot] = value
    return acc
  }, {} as RpcStateMapping)
}

export type SerializeAccountStateOverrideParameters = Omit<StateOverride[number], 'address'>

export type SerializeAccountStateOverrideErrorType =
  | NumberToHexErrorType
  | StateAssignmentConflictErrorType
  | SerializeStateMappingErrorType

export function serializeAccountStateOverride(
  parameters: SerializeAccountStateOverrideParameters,
): RpcAccountStateOverride {
  const { balance, nonce, state, stateDiff, code } = parameters
  const rpcAccountStateOverride: RpcAccountStateOverride = {}
  if (code !== undefined) rpcAccountStateOverride.code = code
  if (balance !== undefined) rpcAccountStateOverride.balance = numberToHex(balance)
  if (nonce !== undefined) rpcAccountStateOverride.nonce = numberToHex(nonce)
  if (state !== undefined) rpcAccountStateOverride.state = serializeStateMapping(state)
  if (stateDiff !== undefined) {
    if (rpcAccountStateOverride.state) throw new StateAssignmentConflictError()
    rpcAccountStateOverride.stateDiff = serializeStateMapping(stateDiff)
  }
  return rpcAccountStateOverride
}

export type SerializeStateOverrideParameters = StateOverride | undefined

export type SerializeStateOverrideErrorType =
  | InvalidAddressErrorType
  | AccountStateConflictErrorType
  | SerializeAccountStateOverrideErrorType

export function serializeStateOverride(parameters?: SerializeStateOverrideParameters): RpcStateOverride | undefined {
  if (!parameters) return undefined
  const rpcStateOverride: RpcStateOverride = {}
  for (const { address, ...accountState } of parameters) {
    if (!isAddress(address, { strict: false })) throw new InvalidAddressError({ address })
    if (rpcStateOverride[getAddress(address)]) throw new AccountStateConflictError({ address: getAddress(address) })
    rpcStateOverride[getAddress(address)] = serializeAccountStateOverride(accountState)
  }
  return rpcStateOverride
}
